import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';

import { formatPrice } from '@dashboard/services/formatting/price';
import { AuthContext } from '@dashboard/context/AuthContext';
import { useCouponQueries } from '@dashboard/hooks/queries/useCouponQueries';
import { couponsKey } from '@dashboard/lib/queryKeys';

import Spinner from '@dashboard/components/Spinner';
import Button from '@dashboard/components/Button';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import EmptyState from '@dashboard/components/EmptyState';
import StatusBadge from '@dashboard/components/StatusBadge';
import CardHeaderActions from '@dashboard/components/CardHeaderActions';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import SortInput from '@dashboard/components/Form/components/SortInput';
import TableRowElement from '@dashboard/components/TableRowElement';

const SORT_DIRECTIONS = {
  ASCENDING: '1',
  DESCENDING: '-1',
};

const SORT_OPTIONS = {
  NEWEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Newest first',
  },
  OLDEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Oldest first',
  },
  LABEL_ASCENDING: {
    value: 'label',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Label A-Z',
  },
  LABELDESCENDING: {
    value: 'label',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Label Z-A',
  },
};

const DiscountsCoupons = () => {
  const [sortOption, setSortOption] = useState(Object.keys(SORT_OPTIONS)[0]);
  const [search, setSearch] = useState('');

  const sort = {
    [SORT_OPTIONS[sortOption].value]: SORT_OPTIONS[sortOption].direction,
  };

  return (
    <Card variant="root">
      <CardHeader variant="root" className="gap-y-2">
        <CardHeaderTitle variant="root">Coupons</CardHeaderTitle>

        <CardHeaderActions>
          <SearchInput
            onChange={setSearch}
            name="search_customers"
            placeholder="Search by label or code"
          />

          <SortInput options={SORT_OPTIONS} onChange={setSortOption} value={sortOption} />
          <Button to="/coupons/new" appearance="primary" style={{ maxHeight: '2.5rem' }}>
            Create coupon <ArrowRightIcon className="w-4 h-4 ml-2 align-baseline" />
          </Button>
        </CardHeaderActions>
      </CardHeader>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header">
          <CardListItemElement size="2xl">Label</CardListItemElement>
          <CardListItemElement size="xl">Code</CardListItemElement>
          <CardListItemElement align="right" textAlign="right">
            Value
          </CardListItemElement>
          <CardListItemElement size="medium">Redemptions</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <CouponsList sort={sort} search={search} />
      </CardContent>
    </Card>
  );
};

const CouponListItem = ({ coupon }) => (
  <CardListItem href={`/coupons/${coupon._id}`} key={coupon._id}>
    <TableRowElement size="2xl">{coupon.label}</TableRowElement>
    <TableRowElement size="xl">{coupon.code}</TableRowElement>

    <TableRowElement align="right" textAlign="right">
      {coupon.amountOff
        ? formatPrice({
            amount: coupon.amountOff,
            currency: coupon.currency,
          })
        : `${coupon.percentOff}%`}
    </TableRowElement>

    <TableRowElement size="medium">
      {coupon.timesRedeemed}
      {' / '}
      {coupon.maxRedemptions ? coupon.maxRedemptions : '∞'}
    </TableRowElement>

    <TableRowElement size="medium">
      <CouponStatusBadge coupon={coupon} />
    </TableRowElement>
  </CardListItem>
);

CouponListItem.propTypes = {
  coupon: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }),
};

const CouponsList = ({ search, sort }) => {
  const { sellerId } = useContext(AuthContext);

  const { getCoupons } = useCouponQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: couponsKey({ search, sort }),
    queryFn: async ({ pageParam }) =>
      await getCoupons(sellerId, {
        search,
        limit: 10,
        skip: pageParam * 10,
        sort: JSON.stringify(sort),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  if (isLoading) {
    return (
      <CardContent variant="root">
        <Spinner />
      </CardContent>
    );
  }

  if (isError) {
    return (
      <CardContent variant="root">
        Unable to load coupons. Please try again or contact support.
      </CardContent>
    );
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty) {
    return (
      <CardContent variant="root">
        <EmptyState>
          <EmptyState.Title>Create your first coupon code</EmptyState.Title>
          <EmptyState.Subtitle>Offer discounts to your customers.</EmptyState.Subtitle>
          <EmptyState.Action to="/coupons/new">Create coupon</EmptyState.Action>
        </EmptyState>
      </CardContent>
    );
  }

  const hasFetchedAllPages = data?.pages?.[data?.pages?.length - 1]?.has_more === false;

  return (
    <CardContent>
      <BottomScrollListener onBottom={fetchNextPage}>
        {data?.pages?.map(data =>
          data?.data.map(coupon => <CouponListItem key={coupon._id} coupon={coupon} />),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="mx-4" />}

      {hasFetchedAllPages && (
        <div className="text-sm text-center text-gray-500 mt-2">No more coupons to show</div>
      )}
    </CardContent>
  );
};

function CouponStatusBadge({ coupon }) {
  if (coupon.deleted) {
    return (
      <StatusBadge status="deleted" className="ml-2 mr-auto self-center">
        Deleted
      </StatusBadge>
    );
  }

  if (coupon.valid) {
    return (
      <StatusBadge status="valid" className="ml-2 mr-auto self-center">
        Valid
      </StatusBadge>
    );
  }

  return (
    <StatusBadge status="expired" className="ml-2 mr-auto self-center">
      Expired
    </StatusBadge>
  );
}

export default DiscountsCoupons;
